// src/components/modals/PrivacyModal.tsx
import React from 'react';
import { Shield, Eye, Lock, Database, X } from 'lucide-react';
import type { ModalProps } from '../../types/components';

export const PrivacyModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-8">
          Last updated: August 19, 2026
        </p>
        
        <div className="space-y-8">
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Information We Collect</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Dairy Translate collects information to provide better services to our users. We collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Account information (name, email) when you register</li>
              <li>Usage data to improve our AI translation services</li>
              <li>Technical information (IP address, browser type) for security</li>
              <li>Dairy operation queries you submit for translation</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">How We Use Your Information</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Provide and improve our DC305 translation services</li>
              <li>Maintain and enhance user experience</li>
              <li>Send service-related communications</li>
              <li>Ensure platform security and prevent abuse</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Data Protection</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Your data security is our priority:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>All data is encrypted in transit and at rest</li>
              <li>We never sell your personal information to third parties</li>
              <li>Access to your data is strictly limited to authorized personnel</li>
              <li>We comply with industry-standard security practices</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              We use third-party service providers to operate Dairy Translate, including Google
              Firebase (authentication, hosting, and data storage) and Google's Gemini API (AI
              translation processing). The dairy operation queries you submit are sent to Gemini to
              generate a response. We encourage you to review Google's privacy policy for details on
              how they handle this data.
            </p>
            <p className="text-gray-700">
              This service is not directed to children under 13, and we do not knowingly collect
              information from children. You may request deletion of your account and associated data
              at any time by contacting us below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:dairytranslate@gmail.com" className="text-blue-600 hover:text-blue-700">
                dairytranslate@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};